/**
 * DnaRadar — a 5-axis radar where each axis is the item COUNT for a DNA category.
 * TASK-474. Ported from the SixEase widget, rebuilt conventionally (props-driven).
 */

import React from 'react';
import type { DnaResponse } from '../../services/knowledgeGraphService';

interface DnaRadarProps {
  dna: DnaResponse;
}

const LABELS = ['Values', 'Exclusions', 'Tilts', 'Life Events', 'Promises'];
const N = 5;
const CX = 160;
const CY = 130;
const R = 92;
const LABEL_R = 112;
const RINGS = [0.25, 0.5, 0.75, 1.0];

function point(axis: number, radius: number): [number, number] {
  // Start at the top (-90°) and go clockwise.
  const angle = (-Math.PI / 2) + (axis * 2 * Math.PI) / N;
  return [CX + radius * Math.cos(angle), CY + radius * Math.sin(angle)];
}

export const DnaRadar: React.FC<DnaRadarProps> = ({ dna }) => {
  const counts = [
    dna.values?.length ?? 0,
    dna.exclusions?.length ?? 0,
    dna.tilts?.length ?? 0,
    dna.life_events?.length ?? 0,
    dna.promises?.length ?? 0,
  ];
  const maxCount = Math.max(...counts, 1);

  const polygon = counts
    .map((c, i) => point(i, (c / maxCount) * R).join(','))
    .join(' ');

  return (
    <svg viewBox="0 0 320 260" className="w-full max-w-xs mx-auto" role="img" aria-label="DNA radar">
      {RINGS.map((ring) => (
        <polygon
          key={ring}
          points={Array.from({ length: N }, (_, i) => point(i, ring * R).join(',')).join(' ')}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      ))}
      {Array.from({ length: N }, (_, i) => {
        const [x, y] = point(i, R);
        return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="#e5e7eb" strokeWidth={1} />;
      })}
      <polygon points={polygon} fill="rgba(124,58,237,0.18)" stroke="#7c3aed" strokeWidth={2} />
      {counts.map((c, i) => {
        const [lx, ly] = point(i, LABEL_R);
        return (
          <text
            key={i}
            x={lx}
            y={ly}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-gray-500"
            fontSize={10}
          >
            {LABELS[i]} ({c})
          </text>
        );
      })}
    </svg>
  );
};

export default DnaRadar;
